/**
 * @author       luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright    Copyright (c) 2023, Zumper
 */

import { TextInput, type TextInputProps } from '@zcode-ui/controls'
import { Button, cn } from '@zcode-ui/shadz-ui'
import React, { type MouseEventHandler } from 'react'
import Pen from '../../icons/PenOutline.svg'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  classNameForInnerWrapper?: TextInputProps['classNameForInnerWrapper']
  classNameForMiddleWrapper?: TextInputProps['classNameForMiddleWrapper']
  customErrorCallback?: TextInputProps['customErrorCallback']
  error?: string
  hideRightElement?: boolean
  rightElement?: React.ReactElement
  rightElementFocusable?: boolean
  isDefault?: boolean
  isDisabled?: boolean
  isInvalid?: boolean
  isRequired?: boolean
  label: string
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement> | React.ClipboardEvent<HTMLInputElement>
  ) => void
  onClick?: MouseEventHandler<HTMLElement>
  onIconClick?: MouseEventHandler<HTMLButtonElement>
  useNativeValidation?: boolean
  value?: string
  width?: string
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      error: errorMessage = '',
      hideRightElement,
      isDefault,
      isDisabled,
      isInvalid,
      isRequired,
      label,
      onChange,
      onClick,
      onIconClick,
      rightElement,
      rightElementFocusable = true,
      value,
      ...rest
    }: InputProps,
    ref
  ) => {
    // biome-ignore lint/correctness/noConstantCondition:
    if (false) {
      // verify that we're not passing any properites to <TextInput /> that it doesn't need
      let x: Exclude<keyof typeof rest, keyof TextInputProps>
      x satisfies never
    }
    const error = isInvalid ? [{ message: errorMessage }] : undefined

    return (
      <div
        className={cn(
          'font-medium text-ellipsis w-full',
          isDefault ? 'text-gray-600' : 'text-gray-950'
        )}
        onClick={onClick}
      >
        <TextInput
          disabled={isDisabled}
          errors={error}
          inputOverlay={
            !hideRightElement
              ? rightElement || (
                  <Button
                    data-testid="end-button"
                    onClick={onIconClick}
                    tabIndex={rightElementFocusable ? undefined : -1}
                    variant="ghost"
                    className="px-1"
                    type="button"
                  >
                    <img alt="Edit" src={Pen} />
                  </Button>
                )
              : null
          }
          isErrorWithIcon
          label={label}
          onChange={onChange}
          ref={ref}
          required={isRequired}
          showClearInput={false}
          value={value}
          width="100%"
          {...rest}
        />
      </div>
    )
  }
)
